'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'
import {
  rotateAuthCacheScope,
  setAuthQueryIdentity,
  syncAuthCacheScopeOwner,
} from '@/hooks/use-auth-cache-scope'
import { currentUserQueryOptions, type UserWithProfile } from '@/hooks/use-user'
import { createClient } from '@/lib/supabase/client'

/**
 * Listens to Supabase auth state changes across tabs.
 * When the user changes (e.g. sign-in on another tab) or signs out,
 * clears cached data and redirects to login.
 */
export const AuthSync = () => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const currentUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const supabase = createClient()
    const userOptions = currentUserQueryOptions()
    let active = true
    let receivedAuthEvent = false

    const resetForUser = (userId: string | null) => {
      rotateAuthCacheScope()
      queryClient.clear()
      currentUserIdRef.current = userId
      if (userId) {
        window.location.reload()
      } else if (window.location.pathname !== '/login') {
        router.push('/login')
      }
    }

    // Capture initial user ID through the shared React Query cache so this
    // listener does not add another Supabase auth/user request on cold routes.
    const cachedUser = queryClient.getQueryData<UserWithProfile | null>(userOptions.queryKey)
    if (cachedUser === undefined) {
      queryClient
        .ensureQueryData(userOptions)
        .then((user) => {
          if (active && !receivedAuthEvent) {
            currentUserIdRef.current = user?.id ?? null
          }
        })
        .catch(() => {
          if (active && !receivedAuthEvent) {
            currentUserIdRef.current = null
          }
        })
    } else {
      currentUserIdRef.current = cachedUser?.id ?? null
    }

    // The workspace stream can hydrate after INITIAL_SESSION. Check that late
    // snapshot too, once hydration finishes, so clearing cannot interleave with
    // the remaining queries being inserted by HydrationBoundary during render.
    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      if (
        !receivedAuthEvent ||
        (event.type !== 'added' && event.type !== 'updated') ||
        event.query.queryKey[0] !== userOptions.queryKey[0]
      ) {
        return
      }
      queueMicrotask(() => {
        if (!active) {
          return
        }
        const user = queryClient.getQueryData<UserWithProfile | null>(userOptions.queryKey)
        if (user && user.id !== currentUserIdRef.current) {
          resetForUser(currentUserIdRef.current)
        }
      })
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (event: string, session: { user?: { id: string } } | null) => {
        receivedAuthEvent = true
        const newUserId = session?.user?.id ?? null
        setAuthQueryIdentity(queryClient, newUserId)
        const hydratedUserId = queryClient.getQueryData<UserWithProfile | null>(
          userOptions.queryKey
        )?.id

        if (event === 'SIGNED_OUT') {
          resetForUser(null)
          return
        }

        if (
          (currentUserIdRef.current !== null && newUserId !== currentUserIdRef.current) ||
          (hydratedUserId !== undefined && newUserId !== hydratedUserId)
        ) {
          // INITIAL_SESSION can already differ from the server snapshot if
          // another tab changed the account while the document was loading.
          // Hydration may also have landed since this listener was registered.
          resetForUser(newUserId)
          return
        }

        if (newUserId) {
          syncAuthCacheScopeOwner(newUserId)
        }

        // Update ref for TOKEN_REFRESHED or initial SIGNED_IN
        currentUserIdRef.current = newUserId
      }
    )

    return () => {
      active = false
      unsubscribeCache()
      subscription.unsubscribe()
    }
  }, [router, queryClient])

  return null
}
